import { VDBText } from "@components/atoms";
import OrderNowDropDown from "@components/atoms/OrderNowDropDown";
import {
  Diamond,
  DisplayConfigurationV2,
  OptionValue,
  SlugDetail,
} from "@src/types/DiamondDetail";
import { JewelryVariation } from "@src/types/JewelryVariationDetails";
import { ProductCategory } from "@utils/constants";
import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../hooks/useScreenOptionsHooks";
import QuantitySelector from "@components/molecules/QuantitySelector";
import currency from "currency.js";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Personalization from "@scenes/configurator/JewelryConfigurator/Personalization";

/**
 * Resolves the pre-selected OptionValue for a display-config row by matching
 * the slug stored in slug_detail[option_type] against the row's option_value list.
 */
const getDefaultOptionValue = (
  slugDetail: SlugDetail | undefined,
  item: DisplayConfigurationV2
): OptionValue | undefined => {
  const slugValue = slugDetail?.[item.option_type];
  if (typeof slugValue !== "string" || !slugValue) return undefined;
  return item.option_value.find((opt) => opt.slug === slugValue);
};

export type CellInfo = {
  quantity: number;
  totalPrice: number | null;
  variantId: number | null;
};

type JewelryConfigurationCellProps = {
  mItem: Diamond;
  variationData?: JewelryVariation;
  isLast?: boolean;
  isFirst?: boolean;
  onSelectionChange?: (optionName: string, value: string) => void;
  onInfoChange?: (info: CellInfo) => void;
  selections?: Record<string, string>;
  showPersonalization?: boolean;
  personalizationTitle?: string;
  personalizationSubOptions?: any;
  personalization?: Record<string, any>;
  onPersonalizationChange?: (values: Record<string, any>) => void;
  showValidation?: boolean;
};

const JewelryConfigurationCell = ({
  mItem,
  variationData,
  isLast = false,
  isFirst = false,
  onSelectionChange,
  onInfoChange,
  selections,
  showPersonalization = false,
  personalizationTitle,
  personalizationSubOptions,
  personalization,
  onPersonalizationChange,
  showValidation = false,
}: JewelryConfigurationCellProps) => {
  const { theme, t, typography } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const allowBackOrder = mItem?.allow_backorder;
  const displayConfig = mItem?.display_configuration_v2;
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    displayConfig?.forEach((item) => {
      if (selections?.[item.option_name]) return;
      if (!item.option_visible) {
        const firstValue = item.option_value?.[0];
        if (firstValue?.label) {
          onSelectionChange?.(item.option_name, firstValue.label);
        }
        return;
      }
      const defaultOpt = getDefaultOptionValue(mItem?.slug_detail, item);
      if (defaultOpt?.label) {
        onSelectionChange?.(item.option_name, defaultOpt.label);
      }
    });
  }, [displayConfig]);

  const variation = (variationData as any)?.response?.body?.variations
    ? variationData
    : (variationData as any)?.data ?? variationData;
  const firstVariation = variation?.response?.body?.variations?.[0];
  const parsedItemPrice = Number(
    firstVariation?.item?.total_sales_price ?? mItem?.total_sales_price
  );
  const unitPrice =
    firstVariation?.data?.price ??
    (Number.isFinite(parsedItemPrice) ? parsedItemPrice : null);
  const variantId =
    firstVariation?.data?.id ??
    firstVariation?.item?.id ??
    (mItem?.id == null || Number.isNaN(Number(mItem.id))
      ? null
      : Number(mItem.id));
  const totalPrice =
    unitPrice != null && !Number.isNaN(unitPrice) ? unitPrice * quantity : null;

  // Report quantity, totalPrice and variantId to parent whenever they change.
  useEffect(() => {
    onInfoChange?.({ quantity, totalPrice, variantId });
  }, [quantity, totalPrice, variantId, variationData]);

  const getKey = (item: DisplayConfigurationV2) =>
    `${item.option_style}_${item.option_name}_${item.option_value
      ?.map((opt) => `${opt.slug}:${opt.enabled}`)
      .join("|")}`;

  const formattedPrice = (mPrice: number) => {
    const totalValue = currency(mPrice, {
      symbol: userInfo?.user?.currency_symbol,
      precision: 0,
    }).format();
    return totalValue;
  };

  return (
    <>
      {isFirst && <Divider theme={theme} />}
      {displayConfig?.map((item) => {
        if (!item.option_visible) return null;

        const key = getKey(item);
        const defaultOptionValue = getDefaultOptionValue(
          mItem?.slug_detail,
          item
        );

        return (
          <OrderNowDropDown
            key={key}
            productType={ProductCategory.JEWELRY}
            configObject={item}
            onSelect={onSelectionChange}
            defaultLabel={defaultOptionValue?.label}
            value={selections?.[item.option_name]}
            showValidation={showValidation}
          />
        );
      })}
      {showPersonalization && (
        <Personalization
          productType={ProductCategory.JEWELRY}
          title={personalizationTitle ?? ""}
          sub_options={personalizationSubOptions}
          engravable={!!mItem?.engravable}
          value={personalization}
          onChange={onPersonalizationChange}
          embedded
          compact
          optional
        />
      )}
      <TotalPriceContainer>
        <LabelContainer>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {t("searchResult.jewelry.totalPrice.text", "Total Price:")}
          </VDBText>
        </LabelContainer>
        <QuantitySelector
          defaultValue={1}
          min={1}
          max={
            allowBackOrder === undefined
              ? 99
              : allowBackOrder
              ? mItem?.max_quantity ?? 99
              : Number(mItem?.available_stock)
          }
          onChange={(e: number) => {
            setQuantity(e);
            onSelectionChange?.("quantity", String(e));
          }}
          label={""}
          fromOrderNow={true}
        />
        <ActualPriceContainer>
          <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
            {totalPrice != null ? formattedPrice(totalPrice) : ""}
          </VDBText>
        </ActualPriceContainer>
      </TotalPriceContainer>
      {!isLast && <Divider theme={theme} />}
    </>
  );
};

export default JewelryConfigurationCell;

const LabelContainer = styled.View`
  width: 35%;
  height: 36px;
  justify-content: center;
`;

const TotalPriceContainer = styled.View`
  width: 100%;
  flex-direction: row;
  padding: 8px 16px 8px;
  align-items: center;
`;

const ActualPriceContainer = styled.View`
  flex: 1;
  align-items: flex-end;
  justify-content: center;
`;

const Divider = styled.View<any>`
  height: 1.7px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
  margin-bottom: 4px;
`;
